// components/EmparejarDialog.jsx
// Relaciona un equipo "padre" (modem/radio/equipo POC) con uno o varios
// equipos "hijo" (chips/baterias) del mismo cliente_servicio — reemplaza al
// viejo emparejar.php + guardarEmparejamiento.php.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Paper,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
} from "@mui/material";
import { getOpcionesEmparejamiento, guardarEmparejamiento } from "../services/emparejamientoService";

const ESTADO_VACIO = {
  soportado: null,
  tipoEquipo: null,
  padreLabel: "",
  hijoLabel: "",
  hijo2Label: "",
  padres: [],
  hijos: [],
  hijos2: [],
};

const subtituloEquipo = (item) =>
  [item.modelo, item.marca, item.emai].filter(Boolean).join(" · ");

// Los equipos de equipo_varios (POC) muestran el EMAI aparte, con su propia
// etiqueta — igual que el emparejar.php original, que le daba su propia
// columna "Emai" en vez de mezclarlo con el modelo.
const subtituloVarios = (item) => [item.modelo, item.marca].filter(Boolean).join(" · ");

export default function EmparejarDialog({ open, onClose, cliente }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [opciones, setOpciones] = useState(ESTADO_VACIO);
  const [padreSeleccionado, setPadreSeleccionado] = useState("");
  const [hijosSeleccionados, setHijosSeleccionados] = useState([]);
  const [hijos2Seleccionados, setHijos2Seleccionados] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveOk, setSaveOk] = useState("");

  const codServicioCliente = cliente?.cod_servicio_cliente;

  // Cuenta cuantos hijos ya apuntan (campo `relacion`) a cada padre — para
  // mostrar en la lista de la izquierda quien ya tiene equipos emparejados,
  // en vez de que el usuario tenga que adivinarlo.
  const relacionadosPorPadre = useMemo(() => {
    const map = new Map();
    for (const h of [...opciones.hijos, ...opciones.hijos2]) {
      const rel = String(h.relacion || "").trim();
      if (!rel) continue;
      map.set(rel, (map.get(rel) || 0) + 1);
    }
    return map;
  }, [opciones.hijos, opciones.hijos2]);

  const cargar = useCallback(async () => {
    if (!codServicioCliente) return;
    setLoading(true);
    setError("");
    setSaveOk("");
    setPadreSeleccionado("");
    setHijosSeleccionados([]);
    setHijos2Seleccionados([]);
    try {
      const { data } = await getOpcionesEmparejamiento(codServicioCliente);
      setOpciones({ ...ESTADO_VACIO, ...data });
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudieron cargar los equipos.");
      setOpciones(ESTADO_VACIO);
    } finally {
      setLoading(false);
    }
  }, [codServicioCliente]);

  useEffect(() => {
    if (open) cargar();
  }, [open, cargar]);

  const toggleHijo = (identificador) => {
    setHijosSeleccionados((prev) =>
      prev.includes(identificador) ? prev.filter((v) => v !== identificador) : [...prev, identificador]
    );
  };

  const toggleHijo2 = (identificador) => {
    setHijos2Seleccionados((prev) =>
      prev.includes(identificador) ? prev.filter((v) => v !== identificador) : [...prev, identificador]
    );
  };

  const hayCambios = Boolean(padreSeleccionado) && (hijosSeleccionados.length > 0 || hijos2Seleccionados.length > 0);

  const handleGuardar = async () => {
    if (!hayCambios) return;
    setSaving(true);
    setError("");
    try {
      await guardarEmparejamiento({
        tipoEquipo: opciones.tipoEquipo,
        equipoPadre: padreSeleccionado,
        hijos: hijosSeleccionados,
        hijos2: hijos2Seleccionados,
      });
      setSaveOk("Emparejamiento guardado correctamente.");
      // Refresca la lista para reflejar las relaciones ya guardadas.
      await cargar();
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo guardar el emparejamiento.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Emparejar equipos
        <Typography variant="body2" color="text.secondary">
          {cliente?.nombre_cliente} — {cliente?.descripcion || cliente?.nombre || ""}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : opciones.soportado === false ? (
          <Alert severity="info">Este tipo de servicio no soporta emparejamiento de equipos.</Alert>
        ) : (
          <>
            {saveOk && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveOk("")}>
                {saveOk}
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              Elige un equipo principal y marca los equipos que se van a relacionar con él.
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: opciones.tipoEquipo === "POC" ? 4 : 5 }}>
                <Paper variant="outlined" sx={{ p: 1.25, height: "100%" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {opciones.padreLabel}
                  </Typography>
                  {!opciones.padres.length ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      Sin equipos disponibles.
                    </Typography>
                  ) : (
                    <RadioGroup
                      value={padreSeleccionado}
                      onChange={(e) => setPadreSeleccionado(e.target.value)}
                    >
                      {opciones.padres.map((p) => {
                        const cantidadRelacionados = relacionadosPorPadre.get(String(p.identificador)) || 0;
                        return (
                          <FormControlLabel
                            key={p.identificador}
                            value={p.identificador}
                            control={<Radio size="small" />}
                            label={
                              <Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                    {p.identificador}
                                  </Typography>
                                  {cantidadRelacionados > 0 && (
                                    <Chip
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      label={`${cantidadRelacionados} emparejado${cantidadRelacionados === 1 ? "" : "s"}`}
                                      sx={{ height: 17, fontSize: "0.62rem", "& .MuiChip-label": { px: 0.6 } }}
                                    />
                                  )}
                                </Box>
                                {opciones.tipoEquipo === "POC" ? (
                                  <>
                                    {subtituloVarios(p) && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                        {subtituloVarios(p)}
                                      </Typography>
                                    )}
                                    {p.emai && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                        EMAI: {p.emai}
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  subtituloEquipo(p) && (
                                    <Typography variant="caption" color="text.secondary">
                                      {subtituloEquipo(p)}
                                    </Typography>
                                  )
                                )}
                              </Box>
                            }
                            sx={{ alignItems: "flex-start", mb: 0.5 }}
                          />
                        );
                      })}
                    </RadioGroup>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: opciones.tipoEquipo === "POC" ? 4 : 7 }}>
                <Paper variant="outlined" sx={{ p: 1.25, height: "100%" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    {opciones.hijoLabel}
                  </Typography>
                  {!opciones.hijos.length ? (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      Sin equipos disponibles.
                    </Typography>
                  ) : (
                    opciones.hijos.map((h) => (
                      <Box key={h.identificador} sx={{ mb: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={hijosSeleccionados.includes(h.identificador)}
                              onChange={() => toggleHijo(h.identificador)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                {h.identificador}
                              </Typography>
                              {subtituloEquipo(h) && (
                                <Typography variant="caption" color="text.secondary">
                                  {subtituloEquipo(h)}
                                </Typography>
                              )}
                            </Box>
                          }
                          sx={{ alignItems: "flex-start" }}
                        />
                        {h.relacion && (
                          <Chip
                            size="small"
                            color="warning"
                            variant="filled"
                            label={`Relacionado con: ${h.relacion}`}
                            sx={{ ml: 4, height: 18, fontSize: "0.65rem" }}
                          />
                        )}
                      </Box>
                    ))
                  )}
                </Paper>
              </Grid>

              {opciones.tipoEquipo === "POC" && (
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper variant="outlined" sx={{ p: 1.25, height: "100%" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      {opciones.hijo2Label}
                    </Typography>
                    {!opciones.hijos2.length ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        Sin equipos disponibles.
                      </Typography>
                    ) : (
                      opciones.hijos2.map((h) => (
                        <Box key={h.identificador} sx={{ mb: 0.5 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={hijos2Seleccionados.includes(h.identificador)}
                                onChange={() => toggleHijo2(h.identificador)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                  {h.identificador}
                                </Typography>
                                {h.emai && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    EMAI: {h.emai}
                                  </Typography>
                                )}
                              </Box>
                            }
                            sx={{ alignItems: "flex-start" }}
                          />
                          {h.relacion && (
                            <Chip
                              size="small"
                              color="warning"
                              variant="filled"
                              label={`Relacionado con: ${h.relacion}`}
                              sx={{ ml: 4, height: 18, fontSize: "0.65rem" }}
                            />
                          )}
                        </Box>
                      ))
                    )}
                  </Paper>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </DialogContent>

      <Divider />
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cerrar
        </Button>
        {opciones.soportado && (
          <Button
            variant="contained"
            onClick={handleGuardar}
            disabled={!hayCambios || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? "Guardando…" : "Emparejar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
